import { HRQuestionGroupWithStatus, HRSubSectionWithStatus, HRSectionWithStatus } from "../models/HR";
import { HRQuestion } from "../messages/HR";
import assertNever from "assert-never";
import { Loading, loading } from "../Loading";
import * as phone_number from "../phone_number";
import { Globals } from "../Globals";

export function evaluateQuestionStatus(question: HRQuestion): false | Loading | null {
    switch (question.type) {
        case "FreeText":
        case "Checkbox": {
            return evaluateCheckBoxQuestionStatus(question);
        }
        case "Text": {
            return evaluateTextQuestionStatus(question);
        }
        case "BirthDate": {
            return evaluateBirthDateQuestionStatus(question);
        }
        case "FullDate":
        case "YearDate":
        case "MonthYearDate": {
            return evaluateDateQuestionStatus(question);
        }
        case "Email": {
            return evaluateEmailQuestionStatus(question);
        }
        case "Numeric": {
            return evaluateNumericQuestionStatus(question);
        }
        case "PhoneNumber": {
            return evaluatePhoneNumberQuestionStatus(question);
        }
        case "SSN": {
            return evaluateSSNQuestionStatus(question);
        }
        case "Radio": {
            return evaluateRadioQuestionStatus(question);
        }
        case "AmericanTest":
        case "DropDown": {
            return evaluateDropDownQuestionStatus(question);
        }
        case "Signature":
        case "GeneralSignature": {
            return evaluateSignatureQuestionStatus(question);
        }
        case "Address": {
            return evaluateAddressQuestionStatus(question);
        }
        case "StartAndEndTime": {
            return evaluateStartAndEndTimeQuestionStatus(question);
        }
        case "Star": {
            return evaluateStarQuestionStatus(question);
        }
        case "Dollar": {
            return evaluateDollarQuestionStatus(question);
        }
        case "Placeholder":
        case "TitleText":
        case "PlainText": {
            return loading();
        }
        case "Document": {
            return evaluateDocumentQuestionStatus(question);
        }
        case "DocumentWithExpiry": {
            return evaluateDocumentWithExpiryQuestionStatus(question);
        }

        case "DownloadableFile": {
            return evaluateDownloadableFileQuestionStatus(question);
        }
        default: {
            return assertNever(question);
        }
    }
}

function evaluateTextQuestionStatus(question: HRQuestion.TextQuestion): false | Loading | null {
    Globals.setAnswerByTemplateId(question.textId, question.answer);
    if (question.answer === null) {
        return null;
    }

    return question.answer.length > 0 ? loading() : false;
}

function evaluateEmailQuestionStatus(question: HRQuestion.EmailQuestion): false | Loading | null {
    if (question.answer === null) {
        Globals.setAnswerByTemplateId(question.textId, null);
        return null;
    }

    const emailRegex = new RegExp(/\S+@\S+\.\S+/);
    if (question.answer.length > 5 && emailRegex.test(question.answer)) {
        Globals.setAnswerByTemplateId(question.textId, question.answer);
        return loading();
    } else {
        Globals.setAnswerByTemplateId(question.textId, null);
        return false;
    }
}

function evaluatePhoneNumberQuestionStatus(question: HRQuestion.PhoneNumberQuestion): false | Loading | null {
    if (question.answer === null) {
        Globals.setAnswerByTemplateId(question.textId, null);
        return null;
    }

    const phoneE164 = phone_number.parsePhoneToE164("US", question.answer);
    if (phoneE164 !== null) {
        Globals.setAnswerByTemplateId(question.textId, phone_number.formatPhone(question.answer));
        return loading();
    } else {
        Globals.setAnswerByTemplateId(question.textId, null);
        return false;
    }
}

function evaluateSSNQuestionStatus(question: HRQuestion.SSNQuestion): false | Loading | null {
    if (question.answer === null) {
        Globals.setLastSSN(null);
        return null;
    }
    const reg = new RegExp(/^(?!(000|666|9))\d{3}(?!00)\d{2}(?!0000)\d{4}$/);
    if (reg.test(question.answer)) {
        Globals.setLastSSN(question.answer);
        return loading();
    } else {
        Globals.setLastSSN(null);
        return false;
    }
}

function evaluateNumericQuestionStatus(question: HRQuestion.NumericQuestion): false | Loading | null {
    if (question.answer === null || question.answer.length === 0) {
        Globals.setAnswerByTemplateId(question.textId, null);
        return null;
    }

    if (+question.answer >= 0) {
        Globals.setAnswerByTemplateId(question.textId, question.answer);
        return loading();
    } else {
        Globals.setAnswerByTemplateId(question.textId, null);
        return false;
    }
}

function evaluateBirthDateQuestionStatus(question: HRQuestion.BirthDateQuestion): Loading | null {
    Globals.setLastBirthDate(question.answer);
    if (question.answer === null) {
        return null;
    }
    return loading();
}

function evaluateDateQuestionStatus(question: HRQuestion.FullDateQuestion | HRQuestion.MonthYearDateQuestion | HRQuestion.YearDateQuestion): Loading | null {
    Globals.setAnswerByTemplateId(question.textId, question.answer);
    if (question.answer === null) {
        return null;
    }
    return loading();
}

function evaluateRadioQuestionStatus(question: HRQuestion.RadioQuestion): Loading | null {
    Globals.setAnswerByTemplateId(question.textId, question.chosenAnswerId);
    if (question.chosenAnswerId === null) {
        return null;
    }
    // TODO check for nested q
    return loading();
}

function evaluateDropDownQuestionStatus(question: HRQuestion.DropDownQuestion | HRQuestion.AmericanTestQuestion): Loading | null {
    Globals.setAnswerByTemplateId(question.textId, question.chosenAnswerId);
    if (question.chosenAnswerId === null) {
        return null;
    }

    return loading();
}

function evaluateCheckBoxQuestionStatus(question: HRQuestion.CheckboxQuestion | HRQuestion.FreeTextQuestion): false | Loading | null {
    if (question.chosenAnswerIds === null) {
        Globals.setAnswerByTemplateId(question.textId, null);
        return null;
    }

    if (question.chosenAnswerIds.length > 0) {
        Globals.setAnswerByTemplateId(question.textId, question.chosenAnswerIds);
        return loading();
    } else {
        Globals.setAnswerByTemplateId(question.textId, null);
        return false;
    }
}

function evaluateSignatureQuestionStatus(question: HRQuestion.SignatureQuestion | HRQuestion.GeneralSignatureQuestion): false | Loading | null {
    if (question.answer === null) {
        Globals.setLastUsedSignature(null);
        return null;
    }
    if (typeof question.answer === "string") {
        if (question.answer.length > 5000) {
            Globals.setLastUsedSignature(question.answer);
            return loading();
        } else {
            Globals.setLastUsedSignature(null);
            return false;
        }
    } else if (typeof question.answer === "object") {
        if (question.answer.signature !== null ) {
            if (question.answer.signature.length > 5000) {
                Globals.setLastUsedSignature(question.answer.signature);
                return loading();
            } else {
                Globals.setLastUsedSignature(null);
                return false;
            }
        } else {
            Globals.setLastUsedSignature(null);
            return null;
        }
    } else {
        Globals.setLastUsedSignature(null);
        return null;
    }
}

function evaluateAddressQuestionStatus(question: HRQuestion.AddressQuestion): false | Loading | null {
    if (question.answer === null) {
        Globals.setLastUsedSignature(null);
        return null;
    }
    if (question.answer.length > 0) {
        Globals.setAnswerByTemplateId(question.textId, question.answer);
        return loading();
    } else {
        Globals.setLastUsedSignature(null);
        return false;
    }
}

function evaluateStartAndEndTimeQuestionStatus(question: HRQuestion.StartAndEndTimeQuestion): false | Loading | null {
    Globals.setAnswerByTemplateId(question.textId, question.answer);
    if (question.answer === null) {
        return null;
    }

    return loading();
}

function evaluateStarQuestionStatus(question: HRQuestion.StarQuestion): false | Loading | null {
    Globals.setAnswerByTemplateId(question.textId, question.answer);
    if (question.answer === null) {
        return null;
    }

    return loading();
}

function evaluateDollarQuestionStatus(question: HRQuestion.DollarQuestion): false | Loading | null {
    if (question.answer === null) {
        Globals.setAnswerByTemplateId(question.textId, null);
        return null;
    }
    if (+question.answer >= 0) {
        Globals.setAnswerByTemplateId(question.textId, question.answer);
        return loading();
    }

    Globals.setAnswerByTemplateId(question.textId, null);
    return false;
}

function evaluateDocumentQuestionStatus(question: HRQuestion.DocumentQuestion): false | Loading | null {
    if (question.answer === null) {
        return null;
    }

    return question.answer.length > 0 ? loading() : false;
}

function evaluateDocumentWithExpiryQuestionStatus(question: HRQuestion.DocumentWithExpiryQuestion): false | Loading | null {
    if (question.answer === null) {
        return null;
    }

    return question.answer.document.length > 0 ? loading() : false;
}

function evaluateDownloadableFileQuestionStatus(question: HRQuestion.DownloadableFileQuestion): false | Loading | null {
    if (question.answer === null) {
        return null;
    }

    return question.answer.length > 0 ? loading() : false;
}

export function evaluateQuestionGroupStatus(group: HRQuestionGroupWithStatus) {
    let totalDoneCount = 0;
    let totalCount = 0;
    let mandatoryDoneCount = 0;
    let mandatoryCount = 0;

    for (const q of group.questions) {
        if (q.question.isHidden) {
            continue;
        }

        totalCount++;
        if (q.status === true) {
            totalDoneCount++;
        }

        // TODO when we have nested question they need to be taken into account
        if (q.question.isMandatory) {
            mandatoryCount++;
            if (q.status === true) {
                mandatoryDoneCount++;
            }
        }
    }

    group.status = (totalCount === 0) ? 0 : Math.round((totalDoneCount / totalCount) * 100);
}

export function evaluateSubSectionStatusAndCompleted(subsection: HRSubSectionWithStatus) {
    let totalDoneCount = 0;
    let totalCount = 0;
    let mandatoryDoneCount = 0;
    let mandatoryCount = 0;

    for (const group of subsection.groups) {
        for (const q of group.questions) {
            if (q.question.isHidden) {
                continue;
            }

            totalCount++;
            if (q.status === true) {
                totalDoneCount++;
            }

            // TODO when we have nested question they need to be taken into account
            if (q.question.isMandatory) {
                mandatoryCount++;
                if (q.status === true) {
                    mandatoryDoneCount++;
                }
            }
        }
    }

    subsection.status = (totalCount === 0) ? 0 : Math.round((totalDoneCount / totalCount) * 100);
    subsection.mandatoryCompleted = mandatoryDoneCount === mandatoryCount;
}

export function evaluateSectionStatus(section: HRSectionWithStatus) {
    let completed = 0;
    for (const sub of section.subSections) {
        if (sub.mandatoryCompleted) {
            completed++;
        }
    }

    section.status = (section.subSections.length === 0) ? 0 : Math.round((completed / section.subSections.length) * 100);
}



// WEBPACK FOOTER //
// ./src/QuestionUtils/StatusEvaluator.ts